import type { ReactNode } from "react";
import { SlideStatement } from "../../../SlideStatement";

// Validation, rebuilt 2026-09-08 (TPF): three zones — the logo band from the
// marketing site (plus NordVPN and EA, both in closing), a production strip
// measured against prod that morning, and a two-column proof ladder
// (revenue & advocacy → what is closing). MAU was dropped: the login-based
// figure is 14 and the old 143 could not be reproduced.
const CDN = "https://public.apptonomy.ai";

type Logo =
  | { name: string; src: string; h: string }
  | { name: string; render: () => ReactNode };

const LOGOS: Logo[] = [
  { name: "Electronic Arts", render: () => <EaMark /> },
  { name: "NordVPN", render: () => <NordVpnLockup /> },
  { name: "GeekLab", src: `${CDN}/geeklab-wordmark-on-light.svg`, h: "h-5 md:h-[26px]" },
  { name: "Pick'em", src: `${CDN}/pickem-wordmark-on-both.svg`, h: "h-5 md:h-[26px]" },
  { name: "Planta", src: `${CDN}/planta-wordmark-on-light.svg`, h: "h-5 md:h-[26px]" },
  { name: "Storytel", src: `${CDN}/storytel-lockup-on-light.svg`, h: "h-5 md:h-[26px]" },
  { name: "TRMNL4", src: `${CDN}/trmnl4-wordmark-on-light.svg`, h: "h-[18px] md:h-[23px]" },
];

export default function Validation() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-10 md:[&_header]:mb-0">
      <SlideStatement eyebrow="Validation">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            Apptonomy makes big claims.{" "}
            <span className="text-blue-700">
              Credibility accelerates the flywheel.
            </span>
          </h1>
          {/* Names what credibility is made of — a restatement, so it stays
              above the rule with the claim. The longer "we're extending our
              brand recognition every day…" paragraph was cut here (TPF,
              2026-09-09): the three zones below ARE that evidence, and the
              template's band has no room for a claim the slide then proves. */}
          <p className="mt-4 text-xl font-medium italic tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            Signed-up logos, case studies, testimonials, and word-of-mouth.
          </p>
        </div>
      </SlideStatement>

      {/* Below the rule — the evidence, in three registers: who uses it, how
          much it runs, and who vouches for it. */}
      <div className="flex flex-col gap-4 md:min-h-0 md:flex-1 md:gap-4 md:pt-6">

      {/* Zone 1 — the logo band, as on the marketing site */}
      <div className="rounded-xl bg-white px-4 py-3 ring-1 ring-slate-200 md:px-6 md:py-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 md:text-xs">
          Trusted by the teams behind
        </p>
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 md:mt-3 md:flex-nowrap md:gap-x-7">
          {LOGOS.map((l) => (
            <span
              key={l.name}
              title={l.name}
              className="inline-flex h-5 shrink-0 items-center md:h-[26px]"
            >
              {"src" in l ? (
                <img src={l.src} alt={l.name} className={`w-auto ${l.h}`} />
              ) : (
                l.render()
              )}
            </span>
          ))}
        </div>
      </div>

      {/* Zone 2 — the platform is genuinely running (prod, non-internal, 2026-09-08) */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-5">
        <MetricTile value="36,000+" label="ASO audits run" note="In production, on real store data" />
        <MetricTile value="181" label="Apps under management" note="Across 135 live projects" />
        <MetricTile value="125" label="Organizations onboarded" note="92 running live projects today" />
      </div>

      {/* Zone 3 — social proof as a five-up proof bar, divided rather than floating */}
      <div className="rounded-xl bg-blue-50 px-5 py-4 ring-1 ring-blue-200 md:px-7 md:py-3.5">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700 md:text-sm">
          Social proof
        </p>

        <div className="mt-4 grid grid-cols-2 gap-y-5 md:mt-3 md:grid-cols-[1fr_1fr_1fr_1fr_1.15fr] md:items-stretch md:gap-y-0">
          <ProofCell
            value="6"
            label="Testimonials"
            note="Named practitioners, sign-off on file. EA’s senior ASO lead measured an 8.56% iOS conversion lift on his own app."
            first
          />
          <ProofCell
            value="3"
            label="Case studies"
            note="Simple Gameplay published: 15–20% first-try conversion lift on their own store data. Pick’em and Go Health in production."
          />
          <ProofCell
            value="5"
            label="Evangelists"
            note="Senior ASO practitioners advocating inside their own networks."
          />
          <ProofCell
            value="12"
            label="Affiliates"
            note="Active on the first-party referral program."
          />
          <ProofCell value="2" label="Marketing partners">
            <div className="mt-3 flex items-center gap-4">
              <img
                src={`${CDN}/geeklab-wordmark-on-light.svg`}
                alt="Geeklab"
                className="h-[15px] w-auto md:h-[17px]"
              />
              <AsaptyWordmark />
            </div>
            <p className="mt-2 text-xs leading-snug text-slate-600 md:text-sm">
              More in development.
            </p>
          </ProofCell>
        </div>
      </div>
      </div>
    </div>
  );
}

// NordVPN: mark (Simple Icons path) + wordmark set in the deck face.
function NordVpnLockup() {
  return (
    <span className="inline-flex items-baseline gap-1 text-[#4687FF]">
      <svg viewBox="0 0.9 24 19.5" className="h-[18px] w-auto md:h-[23px]" aria-hidden="true">
        <path
          fill="currentColor"
          d="M2.2838 21.5414A11.9866 11.9866 0 010 14.4832C0 7.8418 5.3727 2.4586 12 2.4586c6.6279 0 12 5.3832 12 12.0246a11.9853 11.9853 0 01-2.2838 7.0582l-5.7636-9.3783-.5565.9419.5645 2.6186L12 8.9338l-2.45 4.1447.5707 2.6451-2.0764-3.5555-5.7605 9.3733z"
        />
      </svg>
      <span className="text-[20px] font-bold leading-none tracking-tight md:text-[26px]">
        NordVPN
      </span>
    </span>
  );
}

// EA: the circle mark alone (Simple Icons path) — the wordmark is redundant.
function EaMark() {
  return (
    <svg viewBox="0 6.1 24 11.9" className="h-[17px] w-auto text-slate-900 md:h-[22px]" aria-label="EA">
      <path
        fill="currentColor"
        d="M16.635 6.162l-5.928 9.377H4.24l1.508-2.3h4.024l1.474-2.335H2.264L.79 13.239h2.156L0 17.84h12.072l4.563-7.259 1.652 2.66h-1.401l-1.473 2.299h4.347l1.473 2.3H24zm-11.461.107L3.7 8.604l9.52-.035 1.474-2.3z"
      />
    </svg>
  );
}

function AsaptyWordmark() {
  return (
    <svg
      viewBox="0 0 120 20"
      className="h-[15px] w-auto text-slate-900 md:h-[18px]"
      aria-label="Asapty"
    >
      <path fill="currentColor" d="M2.04837 19.3617C1.24609 19.3617 0.657186 19.0855 0.281651 18.5331C-0.0938837 17.9806 -0.0938837 17.29 0.281651 16.4614L6.65721 2.58093C6.96446 1.90763 7.34 1.4156 7.78381 1.10484C8.22762 0.794084 8.74825 0.638706 9.34569 0.638706C9.92607 0.638706 10.4382 0.794084 10.882 1.10484C11.3258 1.4156 11.7013 1.90763 12.0086 2.58093L18.3841 16.4614C18.7767 17.3073 18.7938 18.0065 18.4353 18.559C18.0769 19.0941 17.5136 19.3617 16.7454 19.3617C16.0626 19.3617 15.5335 19.2064 15.1579 18.8956C14.7995 18.5849 14.4752 18.0842 14.185 17.3936L13.3656 15.4255H5.30016L4.48081 17.3936C4.17356 18.0842 3.85777 18.5849 3.53344 18.8956C3.20912 19.2064 2.71409 19.3617 2.04837 19.3617ZM9.29449 5.84387L6.83644 11.7741H11.8037L9.34569 5.84387H9.29449Z" />
      <path fill="currentColor" d="M30.2879 19.4135C29.2466 19.4135 28.1712 19.3099 27.0617 19.1028C25.9692 18.8783 25.0304 18.5676 24.2452 18.1705C23.7843 17.9461 23.46 17.644 23.2722 17.2641C23.0845 16.8843 23.0162 16.4959 23.0674 16.0988C23.1186 15.6845 23.2637 15.3219 23.5027 15.0112C23.7416 14.6831 24.0574 14.4673 24.45 14.3638C24.8426 14.2429 25.2864 14.2947 25.7815 14.5191C26.4301 14.8471 27.1641 15.0975 27.9835 15.2701C28.8028 15.4255 29.571 15.5032 30.2879 15.5032C31.4145 15.5032 32.1997 15.3564 32.6435 15.063C33.1044 14.7522 33.3348 14.3724 33.3348 13.9235C33.3348 13.5264 33.1727 13.207 32.8484 12.9654C32.5411 12.7237 31.9863 12.5165 31.1841 12.3438L28.1883 11.6964C24.9451 10.9886 23.3234 9.27081 23.3234 6.54307C23.3234 5.35184 23.6392 4.30735 24.2708 3.40961C24.9195 2.51187 25.8156 1.8213 26.9593 1.33791C28.103 0.837245 29.4344 0.586914 30.9536 0.586914C31.8412 0.586914 32.7374 0.690499 33.6421 0.897669C34.5639 1.08758 35.3576 1.38107 36.0233 1.77814C36.5354 2.05437 36.8597 2.43418 36.9963 2.91758C37.1329 3.38372 37.1158 3.84122 36.9451 4.29009C36.7744 4.73896 36.4671 5.06698 36.0233 5.27415C35.5966 5.48132 35.0504 5.44679 34.3846 5.17056C33.8725 4.94613 33.3092 4.78212 32.6947 4.67853C32.0802 4.55768 31.4828 4.49726 30.9024 4.49726C29.9465 4.49726 29.2125 4.6699 28.7004 5.01518C28.1883 5.3432 27.9323 5.77481 27.9323 6.31C27.9323 6.68981 28.0859 7.00057 28.3931 7.24227C28.7004 7.48396 29.2466 7.69113 30.0318 7.86378L33.002 8.51119C36.2964 9.21902 37.9437 10.8936 37.9437 13.5351C37.9437 14.7263 37.6279 15.7622 36.9963 16.6426C36.3647 17.5231 35.4771 18.205 34.3334 18.6884C33.1898 19.1718 31.8412 19.4135 30.2879 19.4135Z" />
      <path fill="currentColor" d="M43.975 19.3617C43.1727 19.3617 42.5838 19.0855 42.2083 18.5331C41.8327 17.9806 41.8327 17.29 42.2083 16.4614L48.5838 2.58093C48.8911 1.90763 49.2666 1.4156 49.7104 1.10484C50.1542 0.794084 50.6749 0.638706 51.2723 0.638706C51.8527 0.638706 52.3648 0.794084 52.8086 1.10484C53.2524 1.4156 53.6279 1.90763 53.9352 2.58093L60.3107 16.4614C60.7033 17.3073 60.7204 18.0065 60.3619 18.559C60.0035 19.0941 59.4402 19.3617 58.672 19.3617C57.9892 19.3617 57.4601 19.2064 57.0845 18.8956C56.7261 18.5849 56.4018 18.0842 56.1116 17.3936L55.2922 15.4255H47.2268L46.4074 17.3936C46.1002 18.0842 45.7844 18.5849 45.46 18.8956C45.1357 19.2064 44.6407 19.3617 43.975 19.3617ZM51.2211 5.84387L48.763 11.7741H53.7303L51.2723 5.84387H51.2211Z" />
      <path fill="currentColor" d="M68.0867 19.3617C67.3357 19.3617 66.7553 19.1546 66.3456 18.7402C65.9359 18.3259 65.7311 17.7303 65.7311 16.9534V3.28013C65.7311 2.50324 65.9359 1.90763 66.3456 1.49328C66.7553 1.07894 67.3442 0.871773 68.1123 0.871773H74.4111C76.4595 0.871773 78.0384 1.40696 79.1479 2.47735C80.2746 3.53046 80.8379 4.98929 80.8379 6.85382C80.8379 8.71836 80.2746 10.1858 79.1479 11.2562C78.0384 12.3093 76.4595 12.8359 74.4111 12.8359H70.4424V16.9534C70.4424 17.7303 70.2375 18.3259 69.8279 18.7402C69.4352 19.1546 68.8549 19.3617 68.0867 19.3617ZM70.4424 9.18449H73.5917C75.4353 9.18449 76.357 8.4076 76.357 6.85382C76.357 5.30004 75.4353 4.52315 73.5917 4.52315H70.4424V9.18449Z" />
      <path fill="currentColor" d="M92.3127 19.3617C91.5617 19.3617 90.9813 19.1546 90.5716 18.7402C90.1619 18.3259 89.9571 17.7303 89.9571 16.9534V4.75622H85.9372C84.6399 4.75622 83.9912 4.10881 83.9912 2.814C83.9912 1.51918 84.6399 0.871773 85.9372 0.871773H98.6883C99.9856 0.871773 100.634 1.51918 100.634 2.814C100.634 4.10881 99.9856 4.75622 98.6883 4.75622H94.6684V16.9534C94.6684 17.7303 94.4635 18.3259 94.0538 18.7402C93.6612 19.1546 93.0809 19.3617 92.3127 19.3617Z" />
      <path fill="currentColor" d="M112.288 19.3617C111.537 19.3617 110.957 19.1546 110.547 18.7402C110.138 18.3086 109.933 17.6957 109.933 16.9016V11.049L105.017 3.84985C104.726 3.43551 104.598 2.97801 104.633 2.47735C104.684 1.97668 104.889 1.54508 105.247 1.18253C105.623 0.81998 106.152 0.638706 106.835 0.638706C107.313 0.638706 107.748 0.759555 108.14 1.00125C108.533 1.24295 108.9 1.6314 109.241 2.16659L112.365 6.80203L115.489 2.14069C115.847 1.6055 116.214 1.22569 116.59 1.00125C116.965 0.759555 117.409 0.638706 117.921 0.638706C118.57 0.638706 119.074 0.802716 119.432 1.13074C119.791 1.45876 119.978 1.8731 119.995 2.37376C120.029 2.87442 119.876 3.38372 119.534 3.90164L114.644 11.049V16.9016C114.644 18.5417 113.859 19.3617 112.288 19.3617Z" />
    </svg>
  );
}

function ProofCell({
  value,
  label,
  note,
  first,
  children,
}: {
  value: string;
  label: string;
  note?: string;
  first?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={
        first
          ? "md:pr-6"
          : "md:border-l md:border-blue-200 md:pl-6 md:pr-6 md:last:pr-0"
      }
    >
      <p className="text-[32px] font-bold leading-none tracking-tight text-blue-700 md:text-[44px]">
        {value}
      </p>
      <p className="mt-1.5 text-xs font-bold uppercase tracking-[0.12em] text-slate-900 md:mt-2 md:text-sm">
        {label}
      </p>
      {note ? (
        <p className="mt-1.5 text-xs leading-snug text-slate-600 md:mt-2 md:text-sm">
          {note}
        </p>
      ) : null}
      {children}
    </div>
  );
}

function MetricTile({
  value,
  label,
  note,
}: {
  value: string;
  label: string;
  note: string;
}) {
  return (
    <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 md:p-3.5">
      <p className="text-3xl font-bold leading-none tracking-tight text-blue-700 md:text-4xl">
        {value}
      </p>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-900 md:text-sm">
        {label}
      </p>
      <p className="mt-1 text-xs leading-snug text-slate-500 md:text-[13px]">
        {note}
      </p>
    </div>
  );
}

